/**
 * Procedural tile art. [P5-tileart] [P9-art] [D-06]
 *
 * Every tile face is drawn at runtime to a Canvas 2D texture from its `TileDef` — no external art
 * assets. This module is deliberately the most-fussed-over file in the client: it is the first thing
 * anyone sees, and the one hard constraint (roads must exit the exact centre of an edge and line up
 * across tile boundaries) is load-bearing for the whole board reading as "real".
 *
 * ── Why rotation is never baked into the texture ─────────────────────────────────────────────────
 * Every texture is drawn for the UNROTATED `TileDef` (rotation 0). The mesh itself is rotated in
 * world space to reflect `PlacedTile.rotation`. That means edge alignment is guaranteed by construction
 * — the same drawing routine, unrotated, is reused for every orientation, so there is no separate code
 * path that could draw a road slightly off-centre for one rotation and not another.
 *
 * ── Canvas-space convention ───────────────────────────────────────────────────────────────────────
 * Canvas (0,0) is the NW corner, x increases East, y increases South — which lines up exactly with the
 * sub-edge diagram in `packages/engine/src/types.ts` (N sub-edges run left-to-right across the top).
 *
 * ── Why the [P9-art] pass never touches `tracePath` ──────────────────────────────────────────────
 * The road-realism additions below (ragged edges, wheel ruts) are all sampled from `roadPointAt` —
 * points ALONG the same centreline `tracePath` strokes — offset sideways by a computed normal. They
 * never redefine the centreline itself, so the one thing that must stay pixel-exact across tile
 * boundaries (the stroke `tracePath` produces) is untouched by any of this file's decorative work.
 *
 * ── [lane-sprites] Tony's own artwork, drawn first when available ───────────────────────────────
 * `renderTileTexture` tries `trySpriteCell` before doing any procedural drawing. When a donor cell
 * maps to this tile's canonical type, its artwork is drawn pre-rotated (see `sprite-atlas.ts` for the
 * rotation derivation) so it reads correctly in this same canonical, unrotated orientation — the mesh
 * still does the only real rotation, exactly as for procedural tiles. Procedural drawing (road, city,
 * cloister) only runs when no sprite is available (either the sheet hasn't loaded yet, or — never
 * currently, since all 24 types have art — this type has none), so it never overwrites sprite art.
 */

import { createRng, next as rngNext, type Rng } from '@carc/engine';
import { Side, type TileDef, type TileFeature } from '@carc/engine';
import { trySpriteCell, type SpriteCell } from './sprite-atlas.js';

const FIELD_BASE = '#6d9a4c';
const FIELD_LIGHT = '#8bb567';
const FIELD_DARK = '#4f7536';
const ROAD_EDGE = '#4a3623';
const ROAD_FILL = '#cdb489';
const ROAD_CENTER = '#e0cba0';
const ROAD_FLECK_DARK = '#a68f68';
const ROAD_FLECK_LIGHT = '#e8d6ae';
const ROAD_FLECK_STONE = '#8f8878';
const ROAD_RUT = 'rgba(58, 42, 24, 0.28)';
const CITY_FILL_LIGHT = '#c7a877';
const CITY_WALL_EDGE = '#3c2916';
const CITY_WALL_HIGHLIGHT = '#eed6a5';
const CITY_WALL_SHADOW = 'rgba(18, 11, 4, 0.32)';
const STONE_TONES = ['#c9ac7c', '#b89566', '#d8c093', '#a9835b'] as const;
const MORTAR = '#5c4a30';
const ROOF_TONES = ['#a15b3f', '#8a4a35', '#5c6570'] as const;
const WINDOW_DARK = '#241708';
const CLOISTER_WALL = '#e9ddc2';
const CLOISTER_WALL_SHADOW = '#cbb98f';
const CLOISTER_ROOF = '#8f3a2c';
const CLOISTER_ROOF_DARK = '#6f2c21';
const CLOISTER_DOOR = '#3c2a1c';
const PENNANT_CLOTH = '#c23b3b';
const PENNANT_CLOTH_DARK = '#8a2424';
const PENNANT_GOLD = '#f0d878';
const PENNANT_POLE = '#e8dcc0';

const TAU = Math.PI * 2;

function hashSeed(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

class SeededDraw {
  private rng: Rng;
  constructor(seed: number) {
    this.rng = createRng(seed);
  }
  next(): number {
    const r = rngNext(this.rng);
    this.rng = r.rng;
    return r.value;
  }
  range(lo: number, hi: number): number {
    return lo + this.next() * (hi - lo);
  }
}

// ── Field background ─────────────────────────────────────────────────────────────────────────────

function paintFurrows(ctx: CanvasRenderingContext2D, R: number, rand: SeededDraw): void {
  // A few faint plough-line curves — hints of hedgerow/furrow per the art brief, kept sparse and
  // curved (never straight/band-like) so they can never be mistaken for a road.
  const rows = 3;
  ctx.strokeStyle = FIELD_DARK;
  ctx.globalAlpha = 0.2;
  ctx.lineWidth = Math.max(1, R * 0.005);
  ctx.lineCap = 'round';
  for (let i = 0; i < rows; i++) {
    const y = rand.range(R * 0.15, R * 0.85);
    const amp = rand.range(R * 0.012, R * 0.032);
    const startX = rand.range(0, R * 0.3);
    const midX = startX + R * rand.range(0.25, 0.35);
    const endX = midX + R * rand.range(0.25, 0.35);
    ctx.beginPath();
    ctx.moveTo(startX, y);
    ctx.bezierCurveTo(midX, y + amp, midX, y - amp, endX, y);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function paintFieldTufts(ctx: CanvasRenderingContext2D, R: number, rand: SeededDraw): void {
  // Sparse little bush/tuft clusters — three overlapping soft blobs each, never anything with hard
  // straight edges that could be mistaken for a wall.
  const count = 3;
  for (let i = 0; i < count; i++) {
    const cx = rand.range(R * 0.1, R * 0.9);
    const cy = rand.range(R * 0.1, R * 0.9);
    const blobs = 3;
    for (let j = 0; j < blobs; j++) {
      const ox = rand.range(-R * 0.022, R * 0.022);
      const oy = rand.range(-R * 0.016, R * 0.016);
      const r = rand.range(R * 0.012, R * 0.022);
      ctx.fillStyle = rand.next() > 0.5 ? FIELD_DARK : FIELD_LIGHT;
      ctx.globalAlpha = rand.range(0.3, 0.5);
      ctx.beginPath();
      ctx.ellipse(cx + ox, cy + oy, r, r * 0.8, 0, 0, TAU);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

function paintField(ctx: CanvasRenderingContext2D, R: number, rand: SeededDraw): void {
  ctx.fillStyle = FIELD_BASE;
  ctx.fillRect(0, 0, R, R);

  // Soft mottling so 72 tiles on a board don't read as flat repeated wallpaper. Deterministic per
  // (tile id, drawIndex) via the seeded rand passed in — never re-rolled per frame.
  const blotches = 34;
  for (let i = 0; i < blotches; i++) {
    const cx = rand.range(0, R);
    const cy = rand.range(0, R);
    const radius = rand.range(R * 0.06, R * 0.19);
    const light = rand.next() > 0.5;
    ctx.globalAlpha = rand.range(0.08, 0.2);
    ctx.fillStyle = light ? FIELD_LIGHT : FIELD_DARK;
    ctx.beginPath();
    ctx.ellipse(cx, cy, radius, radius * rand.range(0.6, 1), rand.range(0, TAU), 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // A scatter of short grass-blade dashes for texture at close zoom.
  ctx.strokeStyle = FIELD_DARK;
  ctx.lineWidth = Math.max(1, R * 0.0035);
  ctx.globalAlpha = 0.45;
  const blades = 110;
  for (let i = 0; i < blades; i++) {
    const x = rand.range(R * 0.04, R * 0.96);
    const y = rand.range(R * 0.04, R * 0.96);
    const len = rand.range(R * 0.008, R * 0.02);
    const angle = rand.range(0, TAU);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  paintFurrows(ctx, R, rand);
  paintFieldTufts(ctx, R, rand);
}

// ── Roads ─────────────────────────────────────────────────────────────────────────────────────────

const sideCenter = (side: Side, R: number): [number, number] => {
  switch (side) {
    case Side.North:
      return [R * 0.5, 0];
    case Side.East:
      return [R, R * 0.5];
    case Side.South:
      return [R * 0.5, R];
    case Side.West:
      return [0, R * 0.5];
  }
};

function subEdgeSide(subEdge: number): Side {
  return Math.floor(subEdge / 3) as Side;
}

function tracePath(ctx: CanvasRenderingContext2D, sides: readonly Side[], R: number): void {
  const center: [number, number] = [R * 0.5, R * 0.5];
  ctx.beginPath();
  if (sides.length === 2) {
    const [a, b] = [sideCenter(sides[0] as Side, R), sideCenter(sides[1] as Side, R)];
    const opposite = Math.abs((sides[0] as number) - (sides[1] as number)) === 2;
    ctx.moveTo(a[0], a[1]);
    if (opposite) {
      ctx.lineTo(b[0], b[1]);
    } else {
      ctx.quadraticCurveTo(center[0], center[1], b[0], b[1]);
    }
  } else if (sides.length === 1) {
    const a = sideCenter(sides[0] as Side, R);
    ctx.moveTo(a[0], a[1]);
    ctx.lineTo(center[0], center[1]);
  }
}

/** A point at parameter `t` (0..1) along the same path `tracePath` strokes — used only for the
 * decorative surface flecks below, never for the stroke itself, so alignment is unaffected. */
function roadPointAt(sides: readonly Side[], R: number, t: number): [number, number] {
  const center: [number, number] = [R * 0.5, R * 0.5];
  if (sides.length === 1) {
    const a = sideCenter(sides[0] as Side, R);
    return [a[0] + (center[0] - a[0]) * t, a[1] + (center[1] - a[1]) * t];
  }
  const a = sideCenter(sides[0] as Side, R);
  const b = sideCenter(sides[1] as Side, R);
  const opposite = Math.abs((sides[0] as number) - (sides[1] as number)) === 2;
  if (opposite) return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  const mt = 1 - t;
  return [mt * mt * a[0] + 2 * mt * t * center[0] + t * t * b[0], mt * mt * a[1] + 2 * mt * t * center[1] + t * t * b[1]];
}

/** The unit sideways normal at `t`, from a finite difference of `roadPointAt` — used to offset
 * decorative marks (ruts, ragged edges) sideways off the centreline without ever computing a second,
 * possibly-drifting, "offset curve" of the road itself. */
function roadNormalAt(sides: readonly Side[], R: number, t: number): [number, number] {
  const eps = 0.02;
  const t0 = Math.max(0, t - eps);
  const t1 = Math.min(1, t + eps);
  const [x0, y0] = roadPointAt(sides, R, t0);
  const [x1, y1] = roadPointAt(sides, R, t1);
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy) || 1;
  return [-dy / len, dx / len];
}

/** A hint of worn-track texture: small deterministic flecks scattered along the road surface —
 * a mix of dry-dirt and pale pebble tones for a dirt-and-cobble read rather than flat paint. */
function paintRoadTexture(ctx: CanvasRenderingContext2D, sides: readonly Side[], R: number, rand: SeededDraw): void {
  if (sides.length === 0) return;
  const count = 11;
  for (let i = 0; i < count; i++) {
    const t = (i + 0.5) / count;
    const [x, y] = roadPointAt(sides, R, t);
    const ox = rand.range(-R * 0.024, R * 0.024);
    const oy = rand.range(-R * 0.024, R * 0.024);
    const r = rand.range(R * 0.006, R * 0.015);
    const roll = rand.next();
    ctx.fillStyle = roll > 0.66 ? ROAD_FLECK_LIGHT : roll > 0.33 ? ROAD_FLECK_DARK : ROAD_FLECK_STONE;
    ctx.globalAlpha = rand.range(0.15, 0.32);
    ctx.beginPath();
    ctx.ellipse(x + ox, y + oy, r, r * 0.7, rand.range(0, TAU), 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

/** Two faint parallel wheel-rut lines, each a polyline sampled off the centreline by a fixed lateral
 * offset (plus small jitter) via `roadNormalAt` — never a real offset curve, so no risk of drifting
 * away from (or across) the centreline `tracePath` actually strokes. */
function paintRoadRuts(ctx: CanvasRenderingContext2D, sides: readonly Side[], R: number, rand: SeededDraw): void {
  if (sides.length === 0) return;
  const laneOffset = R * 0.026;
  const steps = 14;
  ctx.strokeStyle = ROAD_RUT;
  ctx.lineWidth = Math.max(1, R * 0.011);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const lane of [-1, 1]) {
    ctx.beginPath();
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const [x, y] = roadPointAt(sides, R, t);
      const [nx, ny] = roadNormalAt(sides, R, t);
      const jitter = rand.range(-R * 0.004, R * 0.004);
      const dist = laneOffset + jitter;
      const px = x + nx * dist * lane;
      const py = y + ny * dist * lane;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
  }
}

/** Small dirt/field-toned blobs jittered right at the road/field boundary so the stroke's clean
 * machine edge breaks up into something irregular — again offset from sampled centreline points via
 * `roadNormalAt`, so the centreline itself is never touched. */
function paintRoadEdgeRag(ctx: CanvasRenderingContext2D, sides: readonly Side[], R: number, rand: SeededDraw): void {
  if (sides.length === 0) return;
  const edge = R * 0.052;
  const count = 24;
  for (let i = 0; i < count; i++) {
    const t = rand.next();
    const [x, y] = roadPointAt(sides, R, t);
    const [nx, ny] = roadNormalAt(sides, R, t);
    const side = rand.next() > 0.5 ? 1 : -1;
    const dist = edge + rand.range(-R * 0.012, R * 0.02);
    const px = x + nx * dist * side;
    const py = y + ny * dist * side;
    const r = rand.range(R * 0.008, R * 0.02);
    ctx.fillStyle = rand.next() > 0.5 ? ROAD_EDGE : FIELD_DARK;
    ctx.globalAlpha = rand.range(0.12, 0.28);
    ctx.beginPath();
    ctx.ellipse(px, py, r, r * 0.7, rand.range(0, TAU), 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function paintRoad(ctx: CanvasRenderingContext2D, feature: TileFeature, R: number, rand: SeededDraw): void {
  const sides = feature.subEdges.map(subEdgeSide);
  const uniqueSides = [...new Set(sides)];

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // A soft wide shadow first, so the road reads as sitting IN the ground rather than painted on top.
  tracePath(ctx, uniqueSides, R);
  ctx.strokeStyle = 'rgba(20, 14, 6, 0.22)';
  ctx.lineWidth = R * 0.19;
  ctx.stroke();

  tracePath(ctx, uniqueSides, R);
  ctx.strokeStyle = ROAD_EDGE;
  ctx.lineWidth = R * 0.15;
  ctx.stroke();

  tracePath(ctx, uniqueSides, R);
  ctx.strokeStyle = ROAD_FILL;
  ctx.lineWidth = R * 0.1;
  ctx.stroke();

  // Ragged edges, cobble/dirt flecks and ruts are all jittered within the fill stroke's half-width
  // (or just outside it, for the edge rag), so no clip is needed to keep them off the field.
  paintRoadEdgeRag(ctx, uniqueSides, R, rand);
  paintRoadTexture(ctx, uniqueSides, R, rand);
  paintRoadRuts(ctx, uniqueSides, R, rand);

  tracePath(ctx, uniqueSides, R);
  ctx.strokeStyle = ROAD_CENTER;
  ctx.lineWidth = R * 0.02;
  ctx.globalAlpha = 0.8;
  ctx.setLineDash([R * 0.048, R * 0.052]);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;
}

// ── Cities ────────────────────────────────────────────────────────────────────────────────────────

/** Rotation (in quarter turns) that maps the canonical shape onto the tile's actual touched sides. */
function cornerRotation(pair: readonly [Side, Side]): number {
  const [a, b] = pair;
  return (b - a + 4) % 4 === 1 ? a : b;
}

function bandRotation(pair: readonly [Side, Side]): number {
  // Canonical band touches North+South (rotation 0); East+West is one quarter-turn.
  return pair.includes(Side.North) || pair.includes(Side.South) ? 0 : 1;
}

function threeRotation(missing: Side): number {
  return missing;
}

const REACH = 0.38;

/** Canonical single wedge (touches North) path, without its own `beginPath` — appendable into a
 * larger compound path (used to punch the notch clip for the 3-sides-touched case). */
function singleWedgePath(ctx: CanvasRenderingContext2D, R: number): void {
  const foot = R * 0.12;
  const apex = R * REACH;
  ctx.moveTo(foot, 0);
  ctx.quadraticCurveTo(R * 0.2, apex * 0.86, R * 0.5, apex);
  ctx.quadraticCurveTo(R * 0.8, apex * 0.86, R - foot, 0);
}

/** Canonical single wedge, as a curve-only path for the wall-edge stroke. */
function singleWedgeCurve(ctx: CanvasRenderingContext2D, R: number): void {
  ctx.beginPath();
  singleWedgePath(ctx, R);
}

function singleWedgeFill(ctx: CanvasRenderingContext2D, R: number): void {
  singleWedgeCurve(ctx, R);
  ctx.closePath();
}

/** Canonical corner wedge (touches North+East) path, without its own `beginPath`. */
function cornerWedgePath(ctx: CanvasRenderingContext2D, R: number): void {
  const reach = R * (1 - REACH);
  const control = R * 0.62;
  ctx.moveTo(reach, 0);
  ctx.lineTo(R, 0);
  ctx.lineTo(R, reach);
  ctx.quadraticCurveTo(control, control, reach, 0);
}

/** Canonical corner wedge, reaching the true shared corner. */
function cornerWedgeCurve(ctx: CanvasRenderingContext2D, R: number): void {
  ctx.beginPath();
  cornerWedgePath(ctx, R);
}

function cornerWedgeFill(ctx: CanvasRenderingContext2D, R: number): void {
  cornerWedgeCurve(ctx, R);
  ctx.closePath();
}

/** Canonical band (touches North+South), a straight-sided strip through the tile. */
function bandFill(ctx: CanvasRenderingContext2D, R: number): void {
  const lo = R * (0.5 - 0.19);
  const hi = R * (0.5 + 0.19);
  ctx.beginPath();
  ctx.moveTo(lo, 0);
  ctx.lineTo(hi, 0);
  ctx.lineTo(hi, R);
  ctx.lineTo(lo, R);
  ctx.closePath();
}

function bandEdges(ctx: CanvasRenderingContext2D, R: number): void {
  const lo = R * (0.5 - 0.19);
  const hi = R * (0.5 + 0.19);
  ctx.beginPath();
  ctx.moveTo(lo, 0);
  ctx.lineTo(lo, R);
  ctx.moveTo(hi, 0);
  ctx.lineTo(hi, R);
}

function withRotation(ctx: CanvasRenderingContext2D, R: number, quarterTurns: number, draw: () => void): void {
  ctx.save();
  ctx.translate(R / 2, R / 2);
  ctx.rotate((quarterTurns * Math.PI) / 2);
  ctx.translate(-R / 2, -R / 2);
  draw();
  ctx.restore();
}

function citySidesTouched(feature: TileFeature): Set<Side> {
  const touched = new Set<Side>();
  for (const side of [Side.North, Side.East, Side.South, Side.West]) {
    if (feature.subEdges.includes(side * 3 + 1)) touched.add(side);
  }
  return touched;
}

/** A visible stone-wall edge with a sense of thickness: a soft dark shadow band (the wall's far
 * face, in shade), a crisp dark line (the top edge), and a light highlight just inside it (the top
 * edge catching the key light) — three concentric strokes on the same curve, widest-and-softest to
 * narrowest-and-crispest, rather than a flat colour change. */
function strokeCityWall(ctx: CanvasRenderingContext2D, R: number, curve: () => void): void {
  curve();
  ctx.strokeStyle = CITY_WALL_SHADOW;
  ctx.lineWidth = Math.max(5, R * 0.032);
  ctx.stroke();

  curve();
  ctx.strokeStyle = CITY_WALL_EDGE;
  ctx.lineWidth = Math.max(3, R * 0.02);
  ctx.stroke();

  curve();
  ctx.strokeStyle = CITY_WALL_HIGHLIGHT;
  ctx.globalAlpha = 0.55;
  ctx.lineWidth = Math.max(1, R * 0.007);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/** Real masonry: a grout-coloured base with individually-toned stone blocks laid in an offset
 * (brick-bond) pattern, inset by a small gap so the grout shows through as mortar lines. Always fills
 * the full `R`×`R` canvas — the caller is expected to have clipped to the city shape first. */
function paintMasonry(ctx: CanvasRenderingContext2D, R: number, rand: SeededDraw): void {
  ctx.fillStyle = MORTAR;
  ctx.fillRect(0, 0, R, R);

  const rowH = R * 0.05;
  const gap = Math.max(1, R * 0.005);
  let row = 0;
  for (let y = -rowH; y < R + rowH; y += rowH) {
    const offset = row % 2 === 0 ? 0 : rowH * 0.55;
    row++;
    let x = -rowH * 2 - offset;
    while (x < R + rowH) {
      const w = rand.range(rowH * 1.3, rowH * 2.2);
      ctx.fillStyle = STONE_TONES[Math.floor(rand.next() * STONE_TONES.length)] as string;
      ctx.fillRect(x + gap / 2, y + gap / 2, Math.max(1, w - gap), rowH - gap);
      x += w;
    }
  }
}

/** A soft directional relight over the masonry — translucent warm highlight toward one corner,
 * translucent dark toward the opposite edge — using ordinary alpha blending (not a canvas blend
 * mode) so it composites predictably over the stone tones beneath it. */
function paintCityLight(ctx: CanvasRenderingContext2D, R: number): void {
  const g = ctx.createRadialGradient(R * 0.42, R * 0.38, R * 0.04, R * 0.5, R * 0.5, R * 0.76);
  g.addColorStop(0, 'rgba(255, 244, 214, 0.22)');
  g.addColorStop(0.55, 'rgba(255, 244, 214, 0)');
  g.addColorStop(1, 'rgba(18, 12, 5, 0.22)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, R, R);
}

function fillCityArea(ctx: CanvasRenderingContext2D, R: number, rand: SeededDraw, clip: () => void): void {
  ctx.save();
  clip();
  paintMasonry(ctx, R, rand);
  paintCityLight(ctx, R);
  ctx.restore();
}

/** One small building: a wall-face block with a footing shadow (so it reads as standing proud of the
 * masonry behind it), a peaked roof in a muted terracotta/slate tone with a ridge highlight, and
 * sometimes a single dark window — so a city reads as buildings behind a wall, not a coloured region. */
function paintCityBuilding(ctx: CanvasRenderingContext2D, R: number, rand: SeededDraw): void {
  const w = rand.range(R * 0.09, R * 0.15);
  const h = rand.range(R * 0.065, R * 0.11);
  const x = rand.range(R * 0.08, R * 0.92);
  const y = rand.range(R * 0.12, R * 0.9);

  ctx.fillStyle = 'rgba(30, 20, 10, 0.28)';
  ctx.fillRect(x - w / 2 + R * 0.008, y - h / 2 + R * 0.01, w, h);

  ctx.fillStyle = CITY_FILL_LIGHT;
  ctx.fillRect(x - w / 2, y - h / 2, w, h);
  ctx.strokeStyle = CITY_WALL_EDGE;
  ctx.globalAlpha = 0.5;
  ctx.lineWidth = Math.max(1, R * 0.004);
  ctx.strokeRect(x - w / 2, y - h / 2, w, h);
  ctx.globalAlpha = 1;

  const roofTone = ROOF_TONES[Math.floor(rand.next() * ROOF_TONES.length)] as string;
  const roofRise = h * rand.range(0.55, 0.85);
  ctx.fillStyle = roofTone;
  ctx.beginPath();
  ctx.moveTo(x - w / 2 - R * 0.008, y - h / 2);
  ctx.lineTo(x, y - h / 2 - roofRise);
  ctx.lineTo(x + w / 2 + R * 0.008, y - h / 2);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 246, 225, 0.3)';
  ctx.lineWidth = Math.max(1, R * 0.0035);
  ctx.beginPath();
  ctx.moveTo(x - w / 2 - R * 0.008, y - h / 2);
  ctx.lineTo(x, y - h / 2 - roofRise);
  ctx.stroke();

  if (rand.next() > 0.35) {
    const ww = w * 0.2;
    const wh = h * 0.4;
    ctx.fillStyle = WINDOW_DARK;
    ctx.fillRect(x - ww / 2, y - wh * 0.3, ww, wh);
  }
}

function decorateCityBuildings(
  ctx: CanvasRenderingContext2D,
  R: number,
  rand: SeededDraw,
  clip: () => void,
  count: number,
): void {
  ctx.save();
  clip();
  for (let i = 0; i < count; i++) paintCityBuilding(ctx, R, rand);
  ctx.restore();
}

function paintCity(ctx: CanvasRenderingContext2D, feature: TileFeature, R: number, rand: SeededDraw): void {
  const touched = citySidesTouched(feature);
  const sides = [...touched];

  if (sides.length === 4) {
    const clip = (): void => {
      ctx.beginPath();
      ctx.rect(0, 0, R, R);
      ctx.clip();
    };
    fillCityArea(ctx, R, rand, clip);
    decorateCityBuildings(ctx, R, rand, clip, 7);
  } else if (sides.length === 3) {
    const missing = [Side.North, Side.East, Side.South, Side.West].find((s) => !touched.has(s)) as Side;
    const rot = threeRotation(missing);
    const clip = (): void => {
      ctx.beginPath();
      ctx.rect(0, 0, R, R);
      singleWedgePath(ctx, R);
      ctx.closePath();
      ctx.clip('evenodd');
    };
    // Cut a field-coloured notch for the one untouched side, clipped to the wedge shape.
    withRotation(ctx, R, rot, () => {
      ctx.save();
      ctx.beginPath();
      singleWedgePath(ctx, R);
      ctx.closePath();
      ctx.clip();
      paintField(ctx, R, rand);
      ctx.restore();
    });
    withRotation(ctx, R, rot, () => {
      fillCityArea(ctx, R, rand, clip);
      decorateCityBuildings(ctx, R, rand, clip, 5);
      strokeCityWall(ctx, R, () => singleWedgeCurve(ctx, R));
    });
  } else if (sides.length === 2) {
    const opposite = Math.abs((sides[0] as number) - (sides[1] as number)) === 2;
    if (opposite) {
      const rot = bandRotation(sides as [Side, Side]);
      const clip = (): void => {
        bandFill(ctx, R);
        ctx.clip();
      };
      withRotation(ctx, R, rot, () => {
        fillCityArea(ctx, R, rand, clip);
        decorateCityBuildings(ctx, R, rand, clip, 3);
        strokeCityWall(ctx, R, () => bandEdges(ctx, R));
      });
    } else {
      const rot = cornerRotation(sides as [Side, Side]);
      const clip = (): void => {
        cornerWedgeFill(ctx, R);
        ctx.clip();
      };
      withRotation(ctx, R, rot, () => {
        fillCityArea(ctx, R, rand, clip);
        decorateCityBuildings(ctx, R, rand, clip, 3);
        strokeCityWall(ctx, R, () => cornerWedgeCurve(ctx, R));
      });
    }
  } else if (sides.length === 1) {
    const rot = sides[0] as number;
    const clip = (): void => {
      singleWedgeFill(ctx, R);
      ctx.clip();
    };
    withRotation(ctx, R, rot, () => {
      fillCityArea(ctx, R, rand, clip);
      decorateCityBuildings(ctx, R, rand, clip, 2);
      strokeCityWall(ctx, R, () => singleWedgeCurve(ctx, R));
    });
  }

  if (feature.pennant) paintPennant(ctx, R, sides);
}

function paintPennant(ctx: CanvasRenderingContext2D, R: number, sides: readonly Side[]): void {
  // Bias the pennant toward the middle of the city mass rather than dead-centre of the whole tile.
  let cx = R * 0.5;
  let cy = R * 0.5;
  if (sides.length > 0 && sides.length < 4) {
    let dx = 0;
    let dy = 0;
    for (const s of sides) {
      if (s === Side.North) dy -= 1;
      if (s === Side.South) dy += 1;
      if (s === Side.East) dx += 1;
      if (s === Side.West) dx -= 1;
    }
    cx = R * 0.5 + dx * R * 0.18;
    cy = R * 0.5 + dy * R * 0.18;
  }

  ctx.save();
  ctx.translate(cx, cy);

  // A soft pale halo behind the shield so it reads clearly against city, field or shadow alike —
  // pennants matter for scoring and players need to spot them at a glance.
  const halo = ctx.createRadialGradient(0, -R * 0.13, 0, 0, -R * 0.13, R * 0.26);
  halo.addColorStop(0, 'rgba(255, 250, 235, 0.6)');
  halo.addColorStop(1, 'rgba(255, 250, 235, 0)');
  ctx.fillStyle = halo;
  ctx.beginPath();
  ctx.arc(0, -R * 0.13, R * 0.26, 0, TAU);
  ctx.fill();

  ctx.strokeStyle = PENNANT_POLE;
  ctx.lineWidth = R * 0.022;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, R * 0.1);
  ctx.lineTo(0, -R * 0.24);
  ctx.stroke();

  // A small heraldic shield rather than a plain flag — a clearer, more legible scoring marker at a
  // glance, per the art brief.
  const w = R * 0.15;
  const h = R * 0.19;
  const sx = -w / 2;
  const sy = -R * 0.25;
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(sx + w, sy);
  ctx.lineTo(sx + w, sy + h * 0.55);
  ctx.quadraticCurveTo(sx + w, sy + h * 0.95, sx + w / 2, sy + h);
  ctx.quadraticCurveTo(sx, sy + h * 0.95, sx, sy + h * 0.55);
  ctx.closePath();

  ctx.save();
  ctx.clip();
  ctx.fillStyle = PENNANT_CLOTH;
  ctx.fillRect(sx, sy, w / 2, h);
  ctx.fillStyle = PENNANT_CLOTH_DARK;
  ctx.fillRect(sx + w / 2, sy, w / 2, h);
  ctx.restore();

  ctx.strokeStyle = CITY_WALL_EDGE;
  ctx.lineWidth = R * 0.01;
  ctx.stroke();

  ctx.fillStyle = PENNANT_GOLD;
  ctx.beginPath();
  ctx.arc(0, sy + h * 0.42, w * 0.13, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = CITY_WALL_EDGE;
  ctx.lineWidth = R * 0.006;
  ctx.stroke();

  ctx.restore();
}

// ── Cloister ──────────────────────────────────────────────────────────────────────────────────────

/** A small abbey rather than a single building: a stone garden wall enclosing the plot, the main
 * wall block with a couple of arched windows, a peaked roof, a suggested bell tower, and a door. */
function paintCloister(ctx: CanvasRenderingContext2D, R: number, rand: SeededDraw): void {
  const cx = R * 0.5;
  const cy = R * 0.53;
  const half = R * 0.135;

  ctx.fillStyle = 'rgba(0,0,0,0.14)';
  ctx.beginPath();
  ctx.ellipse(cx, cy + half * 1.2, half * 1.55, half * 0.55, 0, 0, TAU);
  ctx.fill();

  // Garden wall: a low stone arc enclosing the near side of the abbey plot, with mortar ticks.
  ctx.strokeStyle = CITY_WALL_EDGE;
  ctx.globalAlpha = 0.5;
  ctx.lineWidth = Math.max(2, R * 0.016);
  ctx.beginPath();
  ctx.ellipse(cx, cy + half * 0.25, half * 2.05, half * 1.3, 0, Math.PI * 0.06, Math.PI * 0.94);
  ctx.stroke();
  ctx.globalAlpha = 1;
  const ticks = 10;
  ctx.strokeStyle = 'rgba(255,255,255,0.25)';
  ctx.lineWidth = Math.max(1, R * 0.004);
  for (let i = 0; i < ticks; i++) {
    const a = Math.PI * (0.1 + (0.8 * i) / (ticks - 1));
    const x = cx + Math.cos(a) * half * 2.05;
    const y = cy + half * 0.25 + Math.sin(a) * half * 1.3;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - Math.cos(a) * R * 0.012, y - Math.sin(a) * R * 0.012);
    ctx.stroke();
  }

  // Main wall block, lightly shaded so it reads as lit from one side.
  const wallGrad = ctx.createLinearGradient(cx - half, 0, cx + half, 0);
  wallGrad.addColorStop(0, CLOISTER_WALL_SHADOW);
  wallGrad.addColorStop(1, CLOISTER_WALL);
  ctx.fillStyle = wallGrad;
  ctx.fillRect(cx - half, cy - half * 0.4, half * 2, half * 1.6);
  ctx.strokeStyle = CITY_WALL_EDGE;
  ctx.lineWidth = Math.max(1, R * 0.008);
  ctx.strokeRect(cx - half, cy - half * 0.4, half * 2, half * 1.6);

  // A couple of small arched windows.
  ctx.fillStyle = CLOISTER_DOOR;
  for (const dx of [-half * 0.55, half * 0.58]) {
    const wx = cx + dx;
    const wy = cy - half * 0.02;
    const ww = half * (0.2 + rand.next() * 0.05);
    const wh = half * 0.34;
    ctx.beginPath();
    ctx.moveTo(wx - ww / 2, wy + wh / 2);
    ctx.lineTo(wx - ww / 2, wy - wh / 4);
    ctx.arc(wx, wy - wh / 4, ww / 2, Math.PI, 0);
    ctx.lineTo(wx + ww / 2, wy + wh / 2);
    ctx.closePath();
    ctx.fill();
  }

  // Roof, with a ridge highlight.
  ctx.fillStyle = CLOISTER_ROOF;
  ctx.beginPath();
  ctx.moveTo(cx - half * 1.2, cy - half * 0.4);
  ctx.lineTo(cx, cy - half * 1.7);
  ctx.lineTo(cx + half * 1.2, cy - half * 0.4);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = CLOISTER_ROOF_DARK;
  ctx.lineWidth = Math.max(1, R * 0.006);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.28)';
  ctx.lineWidth = Math.max(1, R * 0.005);
  ctx.beginPath();
  ctx.moveTo(cx - half * 1.15, cy - half * 0.42);
  ctx.lineTo(cx, cy - half * 1.65);
  ctx.stroke();

  // A small bell tower suggested behind the roofline — height jittered per tile so a run of cloister
  // tiles doesn't look stamped from the same die.
  const towerW = half * 0.36;
  const towerH = half * (1.05 + rand.next() * 0.2);
  const tx = cx + half * 0.72;
  const ty = cy - half * 0.4;
  ctx.fillStyle = CLOISTER_WALL_SHADOW;
  ctx.fillRect(tx - towerW / 2, ty - towerH, towerW, towerH);
  ctx.strokeStyle = CITY_WALL_EDGE;
  ctx.lineWidth = Math.max(1, R * 0.006);
  ctx.strokeRect(tx - towerW / 2, ty - towerH, towerW, towerH);
  ctx.fillStyle = CLOISTER_ROOF;
  ctx.beginPath();
  ctx.moveTo(tx - towerW * 0.75, ty - towerH);
  ctx.lineTo(tx, ty - towerH - towerW);
  ctx.lineTo(tx + towerW * 0.75, ty - towerH);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = CITY_WALL_EDGE;
  ctx.beginPath();
  ctx.ellipse(tx, ty - towerH * 0.4, towerW * 0.2, towerW * 0.26, 0, 0, TAU);
  ctx.fill();

  // Door.
  const dw = half * 0.5;
  const dh = half * (0.8 + rand.next() * 0.1);
  const dx0 = cx - dw / 2;
  const dy0 = cy + half * 1.2 - dh;
  ctx.fillStyle = CLOISTER_DOOR;
  ctx.beginPath();
  ctx.moveTo(dx0, dy0 + dh);
  ctx.lineTo(dx0, dy0 + dh * 0.3);
  ctx.arc(cx, dy0 + dh * 0.3, dw / 2, Math.PI, 0);
  ctx.lineTo(dx0 + dw, dy0 + dh);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = CITY_WALL_EDGE;
  ctx.lineWidth = Math.max(1, R * 0.005);
  ctx.stroke();
}

// ── Paper/print grain ────────────────────────────────────────────────────────────────────────────

/** A whole-tile overlay: fine paper-tooth speckle, a few soft ink-offset smudges, and a gentle
 * vignette — tying every terrain together as one printed, slightly imperfect physical object rather
 * than a set of flat colour fills. Purely additive over whatever terrain/features are already drawn,
 * and drawn once per texture (never per frame), so it costs nothing at render time. */
function paintPrintGrain(ctx: CanvasRenderingContext2D, R: number, rand: SeededDraw): void {
  const speckles = 260;
  for (let i = 0; i < speckles; i++) {
    const x = rand.range(0, R);
    const y = rand.range(0, R);
    const s = rand.range(0.6, 1.6);
    ctx.fillStyle = rand.next() > 0.5 ? 'rgba(255,255,255,0.05)' : 'rgba(30,20,10,0.05)';
    ctx.fillRect(x, y, s, s);
  }

  const smudges = 8;
  for (let i = 0; i < smudges; i++) {
    const x = rand.range(0, R);
    const y = rand.range(0, R);
    const r = rand.range(R * 0.08, R * 0.22);
    ctx.globalAlpha = rand.range(0.02, 0.045);
    ctx.fillStyle = rand.next() > 0.5 ? '#2c1d10' : '#fff6df';
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * rand.range(0.6, 1), rand.range(0, TAU), 0, TAU);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  const vignette = ctx.createRadialGradient(R * 0.5, R * 0.5, R * 0.32, R * 0.5, R * 0.5, R * 0.72);
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(20,14,6,0.1)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, R, R);
}

// ── Sprite art [lane-sprites] ────────────────────────────────────────────────────────────────────

/** Draws one already-cropped sprite-sheet cell onto the R×R canvas, pre-rotated so its artwork lines
 * up with this same canonical (rotation-0) orientation the procedural painters use — see the
 * rotation-convention note in `tools/build-sprite-atlas.mjs`. */
function paintSprite(ctx: CanvasRenderingContext2D, R: number, cell: SpriteCell): void {
  ctx.save();
  ctx.translate(R / 2, R / 2);
  ctx.rotate((cell.rotation * Math.PI) / 2);
  ctx.translate(-R / 2, -R / 2);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(cell.image, cell.sx, cell.sy, cell.size, cell.size, 0, 0, R, R);
  ctx.restore();
}

// ── Public API ────────────────────────────────────────────────────────────────────────────────────

export interface TileTextureOptions {
  readonly resolution?: number;
  readonly variationSeed?: number;
}

/**
 * Draw the unrotated face of `def` to a fresh canvas. Called once per placed-tile instance (never
 * cached across positions) so `variationSeed` — typically the tile's `drawIndex` — can give each
 * instance its own mottling without any two tiles of the same type looking identical.
 */
export function renderTileTexture(def: TileDef, opts: TileTextureOptions = {}): HTMLCanvasElement {
  const R = opts.resolution ?? 640;
  const canvas = document.createElement('canvas');
  canvas.width = R;
  canvas.height = R;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  const rand = new SeededDraw(hashSeed(def.id) ^ ((opts.variationSeed ?? 0) * 0x9e3779b1));

  const spriteCell = trySpriteCell(def.id, opts.variationSeed ?? 0);
  if (spriteCell) {
    paintSprite(ctx, R, spriteCell);
  } else {
    paintField(ctx, R, rand);

    for (const feature of def.features) {
      if (feature.kind === 'road') paintRoad(ctx, feature, R, rand);
    }
    for (const feature of def.features) {
      if (feature.kind === 'city') paintCity(ctx, feature, R, rand);
    }
    for (const feature of def.features) {
      if (feature.kind === 'cloister') paintCloister(ctx, R, rand);
    }
  }

  paintPrintGrain(ctx, R, rand);

  // A faint inset border helps each tile read as a distinct object even where terrain matches its
  // neighbour exactly (field-to-field), echoing the "sitting on a surface" brief for [P5-scene].
  ctx.strokeStyle = 'rgba(0,0,0,0.08)';
  ctx.lineWidth = Math.max(1, R * 0.006);
  ctx.strokeRect(1, 1, R - 2, R - 2);

  return canvas;
}

const textureCache = new Map<string, HTMLCanvasElement>();

/** Cached by (tile id, variation seed) — used for repeated lookups like the HUD preview. */
export function cachedTileTexture(def: TileDef, variationSeed: number): HTMLCanvasElement {
  const key = `${def.id}:${variationSeed}`;
  let canvas = textureCache.get(key);
  if (!canvas) {
    canvas = renderTileTexture(def, { variationSeed });
    textureCache.set(key, canvas);
  }
  return canvas;
}
